/**
 * API Lauria: endpoints liés aux utilisateurs.
 * Responsabilité: exposer des hooks RTK Query pour interroger le backend Lauria.
 */
import { baseApi } from 'core/api/base'
import type { HydraCollection } from 'lauria/types/hydra'
import type {
  CreateUtilisateurInput,
  GetUtilisateursParams,
  UpdateUtilisateurInput,
  UpdateUtilisateurTagsInput,
  Utilisateur,
  UtilisateurBase,
  UtilisateurDetails,
  UtilisateurListItem,
} from 'lauria/types/utilisateur'
import { generatePath } from 'react-router'

const UTILISATEURS_ME_ENDPOINT = '/utilisateurs/me'
const CLIENT_UTILISATEURS_ENDPOINT = '/clients/:idClient/utilisateurs'
const CLIENT_UTILISATEUR_ENDPOINT =
  '/clients/:idClient/utilisateurs/:idUtilisateur'
const UTILISATEUR_ENDPOINT = '/utilisateurs/:idUtilisateur'
const UTILISATEUR_CLIENT_ENDPOINT =
  '/utilisateurs/:idUtilisateur/clients/:idClient'
const UTILISATEUR_TAGS_ENDPOINT =
  '/clients/:idClient/utilisateurs/:idUtilisateur/tags'

function buildUtilisateursSearchParams(params: GetUtilisateursParams): string {
  const searchParams = new URLSearchParams()

  if (params.itemsPerPage !== undefined) {
    searchParams.append('itemsPerPage', params.itemsPerPage.toString())
  }
  if (params.page !== undefined) {
    searchParams.append('page', params.page.toString())
  }
  if (params.search !== undefined && params.search !== '') {
    searchParams.append('search', params.search)
  }
  if (params.compteService !== undefined) {
    searchParams.append('compteService', String(params.compteService))
  }

  ;(params.tagIds ?? []).forEach((tagId) => {
    // Convention backend: tableau sérialisé via `tagIds[]` (pas `tagIds=1&tagIds=2`).
    searchParams.append('tagIds[]', tagId.toString())
  })

  const queryString = searchParams.toString()
  return queryString !== '' ? `?${queryString}` : ''
}

function buildCreateUtilisateurBody(
  input: CreateUtilisateurInput
): CreateUtilisateurInput {
  if (input.compteService) {
    const { idKeycloak, nomUtilisateur, compteService } = input
    return { idKeycloak, nomUtilisateur, compteService }
  }

  return input
}

const utilisateursApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Utilisateur connecté (réponse "Lauria", distincte de l'utilisateur Keycloak).
     *
     * Note: un endpoint similaire existe déjà dans `src/core/api/me.ts` (`getMyUser`).
     * Celui-ci est exposé ici pour regrouper la couche "utilisateurs" côté `src/lauria`.
     */
    getUtilisateurMe: builder.query<Utilisateur, void>({
      query: () => ({ url: UTILISATEURS_ME_ENDPOINT }),
      providesTags: [{ type: 'Utilisateur', id: 'ME' }],
    }),

    getUtilisateurByClient: builder.query<
      UtilisateurDetails,
      { idClient: number; idUtilisateur: number }
    >({
      query: ({ idClient, idUtilisateur }) =>
        generatePath(CLIENT_UTILISATEUR_ENDPOINT, {
          idClient: idClient.toString(),
          idUtilisateur: idUtilisateur.toString(),
        }),
      providesTags: (result, error, { idUtilisateur }) => [
        { type: 'Utilisateur', id: idUtilisateur },
      ],
    }),

    getUtilisateursByClient: builder.query<
      HydraCollection<UtilisateurListItem>,
      { idClient: number; params?: GetUtilisateursParams }
    >({
      query: ({ idClient, params = {} }) => {
        const url = generatePath(CLIENT_UTILISATEURS_ENDPOINT, {
          idClient: idClient.toString(),
        })
        const queryString = buildUtilisateursSearchParams(params)
        return `${url}${queryString}`
      },
      providesTags: (result) =>
        result?.member
          ? [
              ...result.member.map(({ idUtilisateur }) => ({
                type: 'Utilisateur' as const,
                id: idUtilisateur,
              })),
              { type: 'Utilisateur', id: 'LIST' },
            ]
          : [{ type: 'Utilisateur', id: 'LIST' }],
    }),

    createUtilisateur: builder.mutation<
      UtilisateurBase,
      { idClient: number } & CreateUtilisateurInput
    >({
      query: ({ idClient, ...input }) => {
        const body = buildCreateUtilisateurBody(input)
        return {
          url: generatePath(CLIENT_UTILISATEURS_ENDPOINT, {
            idClient: idClient.toString(),
          }),
          method: 'POST',
          body,
        }
      },
      invalidatesTags: [{ type: 'Utilisateur', id: 'LIST' }],
    }),

    updateUtilisateur: builder.mutation<
      Utilisateur,
      { idUtilisateur: number; body: UpdateUtilisateurInput }
    >({
      query: ({ idUtilisateur, body }) => ({
        url: generatePath(UTILISATEUR_ENDPOINT, {
          idUtilisateur: idUtilisateur.toString(),
        }),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { idUtilisateur }) => [
        { type: 'Utilisateur', id: idUtilisateur },
        { type: 'Utilisateur', id: 'LIST' },
      ],
    }),

    assignUtilisateurToClient: builder.mutation<
      void,
      { idUtilisateur: number; idClient: number }
    >({
      query: ({ idUtilisateur, idClient }) => ({
        url: generatePath(UTILISATEUR_CLIENT_ENDPOINT, {
          idUtilisateur: idUtilisateur.toString(),
          idClient: idClient.toString(),
        }),
        method: 'PUT',
        // 204 No Content: on évite toute tentative de parse JSON côté RTK Query.
        responseHandler: async (response: Response) => {
          await response.text()
          return undefined
        },
      }),
      invalidatesTags: (result, error, { idUtilisateur }) => [
        { type: 'Utilisateur', id: idUtilisateur },
        { type: 'Utilisateur', id: 'LIST' },
      ],
    }),

    updateUtilisateurTags: builder.mutation<
      UtilisateurDetails,
      {
        idClient: number
        idUtilisateur: number
        body: UpdateUtilisateurTagsInput
      }
    >({
      query: ({ idClient, idUtilisateur, body }) => ({
        url: generatePath(UTILISATEUR_TAGS_ENDPOINT, {
          idClient: idClient.toString(),
          idUtilisateur: idUtilisateur.toString(),
        }),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { idUtilisateur }) => [
        { type: 'Utilisateur', id: idUtilisateur },
        { type: 'Utilisateur', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useGetUtilisateurMeQuery,
  useGetUtilisateurByClientQuery,
  useGetUtilisateursByClientQuery,
  useCreateUtilisateurMutation,
  useUpdateUtilisateurMutation,
  useAssignUtilisateurToClientMutation,
  useUpdateUtilisateurTagsMutation,
} = utilisateursApi
export { utilisateursApi }
